import { useFlags } from "launchdarkly-react-client-sdk";
import { maxBy, minBy } from "lodash";
import { useCallback, useMemo } from "react";

import {
  Features,
  Limits,
  PlanName,
  Product,
  PurchasablePlan,
  useAllPlansQuery,
  useCurrentPlanStateQuery,
} from "@/apollo/types";
import dayjs from "@/utils/date";

const DefaultFeatures: Features = {
  aiAssistantHistory: false,
  auditLogs: false,
  columnHashing: false,
  externalAuthorization: false,
  lineage: true,
  modelTesting: false,
  onDemandSyncs: false,
  orchestration: false,
  changeDataCapture: false,
  connectApi: false,
  softDeletes: false,
  marRulesV2: false,
  multiDestinationElt: false,
};

const DefaultLimits = {
  connectors: 10,
  users: 10,
  workspaces: 5,
  rows: 20_000_000,
  minSyncIntervalInMinutes: 24 * 60,
};

export function useCurrentPlan() {
  const planStateQuery = useCurrentPlanStateQuery();

  const currentPlanState = planStateQuery.data?.currentPlanState;

  const planName = currentPlanState?.plan?.name ?? PlanName.Premium;
  const features = currentPlanState?.plan?.features ?? DefaultFeatures;
  const limits = currentPlanState?.plan?.limits ?? DefaultLimits;

  const currentUsagePeriod = useMemo(() => {
    if (!currentPlanState) return null;
    return {
      start: dayjs.utc(currentPlanState.currentUsagePeriod.start).toDate(),
      end: dayjs.utc(currentPlanState.currentUsagePeriod.end).toDate(),
    };
  }, [currentPlanState]);

  return {
    refetch: planStateQuery.refetch,
    loading: planStateQuery.loading,
    ...currentPlanState?.plan,
    name: planName,
    features,
    limits,
    subscription: currentPlanState?.subscription,
    currentUsagePeriod,
  };
}

export function useWeldMinimumPlanForFeature(feature: keyof Features): {
  minimumPlanName: PlanName;
  loading: boolean;
} {
  const { activePlans, loading } = useWeldPlans();
  const minimumPlanName =
    activePlans.find((x) => x.features[feature])?.name ?? PlanName.Business;
  return { minimumPlanName, loading };
}

export function useWeldPlans() {
  const { data: allPlansData, loading } = useAllPlansQuery();

  // These flags are used to filter the displayed plans for the current account and are used as a sales hack.
  const { displayOnlyBusinessPlan, displayOnlyPremiumPlan } = useFlags();

  const plans = useMemo(() => {
    const plans = allPlansData?.allPlans ?? [];
    if (displayOnlyBusinessPlan) {
      return plans.filter((x) => x.name === PlanName.Business);
    }
    if (displayOnlyPremiumPlan) {
      return plans.filter((x) => x.name === PlanName.Premium);
    }
    return plans;
  }, [displayOnlyBusinessPlan, displayOnlyPremiumPlan, allPlansData]);

  const activePlans = plans.filter((x) => x.isLegacyPlan !== true);

  return {
    loading,
    plans,
    activePlans,
    getApplicablePlan: useCallback(
      (
        targetFeatures: Partial<
          Pick<Limits, "minSyncIntervalInMinutes" | "connectors" | "users">
        >,
      ) => {
        if (targetFeatures.minSyncIntervalInMinutes) {
          return featureTests.minSyncIntervalInMinutes(
            targetFeatures.minSyncIntervalInMinutes,
            activePlans,
          );
        }
        if (targetFeatures.connectors) {
          return featureTests.connectors(
            targetFeatures.connectors,
            activePlans,
          );
        }

        return null;
      },
      [activePlans],
    ),
  };
}

type TargetPlan = {
  plan: PurchasablePlan;
  addOn: Product | null;
};

type FeatureTests = {
  [
    K in keyof Pick<Limits, "minSyncIntervalInMinutes" | "connectors" | "users">
  ]: (arg: Limits[K], plans: PurchasablePlan[]) => TargetPlan | null;
};

const getApplicablePlanForSyncFrequency: FeatureTests["minSyncIntervalInMinutes"] =
  (value, plans) => {
    let applicableCombo: TargetPlan | null = null;

    // Which plan would allow this option?
    const targetPlan = maxBy(
      plans.filter((plan) => plan.limits.minSyncIntervalInMinutes <= value),
      (plan) => plan.limits.minSyncIntervalInMinutes,
    );

    if (targetPlan) {
      applicableCombo = {
        plan: targetPlan,
        addOn: null,
      };
    }
    return applicableCombo;
  };

const getApplicablePlanForConnectorsLimit: FeatureTests["connectors"] = (
  value,
  plans,
) => {
  // Which plan would allow this option?
  let targetPlan = minBy(
    plans.filter((plan) => {
      const limit = plan.limits.connectors;
      const configurableLimit = plan.upgradableLimits?.maxConnectors;
      return (configurableLimit || limit) >= value;
    }),
    (plan) => plan.limits.connectors,
  );

  if (!targetPlan) {
    targetPlan = plans.find((x) => x.limits.connectors === -1);
  }

  if (targetPlan) {
    return {
      plan: targetPlan,
      addOn: null,
    };
  }
  return null;
};

const getApplicablePlanForUsersLimit: FeatureTests["users"] = (
  value,
  plans,
) => {
  // Which plan would allow this option?
  let targetPlan = minBy(
    plans.filter((plan) => plan.limits.users >= value),
    (plan) => plan.limits.users,
  );

  if (!targetPlan) {
    targetPlan = plans.find((x) => x.limits.users === -1);
  }

  if (targetPlan) {
    return {
      plan: targetPlan,
      addOn: null,
    };
  }
  return null;
};

const featureTests: FeatureTests = {
  minSyncIntervalInMinutes: getApplicablePlanForSyncFrequency,
  connectors: getApplicablePlanForConnectorsLimit,
  users: getApplicablePlanForUsersLimit,
};
